import React, { useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Alert,
  Slider,
  Stack,
  Chip,
  Card,
  CardContent,
} from "@mui/material";
import { useWeddingWizard } from "../../../context/WeddingWizardContext";

export default function GuestConfigStep() {
  const {
    state,
    // planner hint
    setGuestsForSizing,
    // cake controls
    setCakeInclude,
    setCakeTiers,
    setCompositeKey,
    setCakeStyleId,
  } = useWeddingWizard();

  const eaters = Number(state?.planner?.guestsForSizing ?? 0);

  const presets = useMemo(() => [10, 20, 30, 40, 50, 75, 100], []);

  const handleSet = (val) => {
    const n = Math.max(0, Math.floor(Number(val) || 0));
    setGuestsForSizing(n);

    // Auto-toggle cake include based on intent
    if (n === 0) {
      // Turn cake off & clear selections to avoid stale state
      setCakeInclude(false);
      setCompositeKey(null);
      setCakeStyleId(null);
      setCakeTiers([]);
    } else if (!state.cake?.include) {
      // Ensure cake is enabled if they plan to serve slices
      setCakeInclude(true);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
        How many people will eat cake?
      </Typography>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        We only need the number of guests who will be served a slice of wedding
        cake. If you have 150 attendees but only 20 will eat cake, enter{" "}
        <strong>20</strong>.
      </Alert>

      <Card variant="outlined" sx={{ borderRadius: 3, mb: 2 }}>
        <CardContent sx={{ p: 2.25 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Enter cake eaters
              </Typography>
              <TextField
                type="number"
                fullWidth
                value={Number.isFinite(eaters) ? eaters : 0}
                onChange={(e) => handleSet(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                placeholder="e.g., 20"
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}>
                This helps us recommend the right cake configuration.
              </Typography>
            </Grid>

            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Or use the slider
              </Typography>
              <Slider
                value={Number.isFinite(eaters) ? eaters : 0}
                min={0}
                max={300}
                step={1}
                onChange={(_, v) => handleSet(Array.isArray(v) ? v[0] : v)}
                valueLabelDisplay="auto"
              />
            </Grid>
          </Grid>

          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            Quick picks
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {presets.map((p) => (
              <Chip
                key={p}
                label={p}
                onClick={() => handleSet(p)}
                color={eaters === p ? "primary" : "default"}
                variant={eaters === p ? "filled" : "outlined"}
                clickable
                sx={{ borderRadius: 2 }}
              />
            ))}
            <Chip
              label="0 (no cake)"
              onClick={() => handleSet(0)}
              color={eaters === 0 ? "primary" : "default"}
              variant={eaters === 0 ? "filled" : "outlined"}
              clickable
              sx={{ borderRadius: 2 }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="body2" color="text.secondary">
        You can change this anytime; we’ll size the cake to match this number.
      </Typography>
    </Box>
  );
}
